import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { oidcUserManager } from '../../services/oidcService';
import { setDesktopAssertion } from '../../services/desktopAssertion';
import { refreshDesktopAppSession } from '../../services/appSession';
import { useSessionStore } from '../../stores/session/sessionStore';
import { useAppSessionStore } from '../../stores/session/appSessionStore';
import { useTenantStore } from '../../stores/tenantStore';
import { useLanguageStore } from '../../stores/languageStore';
import { decodeDesktopLaunchToken, extractLaunchedHighWaterMark, extractLaunchedUsername } from '../../utils/bridge/desktopLaunchToken';
import { applyBridgeLaunchContext } from '../../services/bridgeLaunchContext';
import { TranslationKey } from '../../i18n/translationKeys';

/**
 * Entry route for Acomba Desktop SSO launches (`/desktop?src=<assertion>`).
 *
 * Fast path (persisted session reuse): when a desktop app session already exists in localStorage for
 * the *same* société and licence — `icIdentifiantPublique` *and* `licenseCIU` decoded from `src` both
 * match the persisted session's — silently reuses it via `refreshDesktopAppSession()` instead of
 * forcing an interactive OIDC round-trip. Any mismatch, undecodable token, or failed refresh falls
 * through to the flow below. On success, tenantStore is re-synced from the fresh launch token
 * (icIdentifiantPublique, highWaterMark, isStaleCopy recomputed by desktop-refresh) exactly like the
 * interactive flow's `setDesktopTenant` call in SigninCallback.tsx — otherwise those fields would keep
 * relaying the *previous* launch's values to the stale-copy check (SC-11) on every subsequent sync.
 *
 * Interactive fallback (unchanged behaviour): reads the assertion from `?src`, carries it in the OIDC
 * `state` object, and starts the standard Identity Server login. The `state` survives the OIDC
 * round-trip via oidc-client-ts's localStorage-backed state store (set on oidcUserManager).
 * `/signin-callback` reads it back from `user.state.src` and continues to POST /connect/desktop-auth.
 *
 * The fallback lives here — not in useAuthGuard's generic desktop-reuse branch — because that branch's
 * own fallback calls `signinRedirect()` without `state`, which drops the assertion and bounces the
 * retry to the AcombaGo tenant picker (the bug fixed by PR #249). Only this explicit
 * `setDesktopAssertion` + `signinRedirect({ state: { src } })` fallback is safe from `/desktop?src=`.
 */
export const DesktopAuth: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const redirectStarted = useRef(false);

    useEffect(() => {
        if (redirectStarted.current) return;
        redirectStarted.current = true;

        void (async () => {
            const src = new URLSearchParams(window.location.search).get('src') ?? undefined;

            // Applied before either path below, because the bridge is already armed with this nonce: a
            // sign-in that is slow or never completes would otherwise leave every bridge call refused.
            applyBridgeLaunchContext(src);

            if (src !== undefined && await tryReuseDesktopSession(src)) {
                // Stash the assertion here too (fast path, not just the interactive fallback below) so
                // it is available later if the user signs out: SignoutCallback re-carries it through the
                // login redirect so the post-logout sign-in resumes this société instead of dropping into
                // the AcombaGO tenant picker (PR #261 review).
                setDesktopAssertion(src);
                void navigate('/', { replace: true });
                return;
            }

            // Persist the assertion so a retry can resume the desktop flow even if the OIDC callback throws
            // and loses the assertion carried in the login state (see services/desktopAssertion.ts).
            if (src !== undefined) setDesktopAssertion(src);

            void oidcUserManager.signinRedirect({ state: src !== undefined ? { src } : undefined });
        })();
    }, [navigate]);

    return (
        <div className="flex items-center justify-center min-h-screen">
            <p className="text-primary-600">{t(TranslationKey.DesktopSession.Loading)}</p>
        </div>
    );
};

/**
 * Attempts the silent-reuse fast path for a fresh `/desktop?src=` launch: the persisted session must
 * be a desktop session, hold a refresh token, and be for the same société, licence, *and* Acomba
 * Usager as the new launch token. A licence transfer/re-registration can change `licenseCIU` while
 * `icIdentifiantPublique` stays the same, so both must match (see `ResolveAcombaAsync`, which resolves
 * the workspace from both identifiers) — and a *different Usager* opening the app under the same
 * société/licence (with or without restarting Acomba) must not silently inherit the previous Usager's
 * session, access decision, and onboarding consent — this fast path bypasses the desktop-auth call
 * that threads the Usager's username, so it needs its own check. Returns false — never throws — on
 * any mismatch, undecodable token, or failed refresh, so the caller conservatively falls through to the
 * interactive flow rather than reuse a session on ambiguous input.
 */
async function tryReuseDesktopSession(src: string): Promise<boolean> {
    const { sessionKind } = useSessionStore.getState();
    const { appRefreshToken } = useAppSessionStore.getState();
    if (sessionKind !== 'desktop' || !appRefreshToken) return false;

    const decoded = decodeDesktopLaunchToken(src);
    if (decoded === null || decoded.icIdentifiantPublique === null) return false;

    const { launchedIcIdentifiantPublique, launchedLicenseCiu, launchedUsername } = useTenantStore.getState();
    if (decoded.icIdentifiantPublique !== launchedIcIdentifiantPublique) return false;
    if ((decoded.licenseCIU ?? null) !== launchedLicenseCiu) return false;
    if ((decoded.username ?? null) !== launchedUsername) return false;

    const decodedHighWaterMark = extractLaunchedHighWaterMark(src);
    const decodedUsername = extractLaunchedUsername(src);
    const result = await refreshDesktopAppSession(decodedHighWaterMark);
    if (result === null) return false;

    // Re-sync tenantStore from *this* launch's token — the same fields SigninCallback.tsx's
    // setDesktopTenant call persists on the interactive flow — so launchedHighWaterMark/isStaleCopy
    // don't keep relaying a stale previous launch's values (see buildBridgeSyncPayload.ts, which reads
    // launchedHighWaterMark straight off tenantStore for the SC-11 stale-copy check). Uses the narrower
    // syncDesktopLaunchContext (not setDesktopTenant) so tenantCode/companyName/ciu are left untouched —
    // the launch token never carries a company name, so there is nothing fresh to apply there, and
    // reading-then-writing-back companyName here served no purpose (and could overwrite it with '' if
    // this ran before the persisted store had rehydrated).
    useTenantStore.getState().syncDesktopLaunchContext(
        result.isStaleCopy, decoded.icIdentifiantPublique, decodedHighWaterMark, decoded.licenseCIU ?? null, decodedUsername);
    // Every launch re-asserts Acomba's working language, so switching it in Acomba is reflected on the
    // next launch rather than being pinned by the language persisted on the first one. Applied after the
    // refresh, never before: setLang PUTs the preference and needs the app session's auth headers.
    if (decoded.lang) useLanguageStore.getState().setLang(decoded.lang);
    return true;
}
